export const ROUTES = {
  admin: "/admin",
  legacyAdmin: "/legacyAdmin",
  partnerAdmin: "/partnerAdmin",
  auth: "/auth",
  destinations: "/destinations",
};

/**
 * Which portal a HOST lands on, and which portals it refuses to serve at all.
 *
 * One CRA bundle serves three consoles off one `index.html`, and the subdomain
 * is what picks one: `admin-pat.zenvoya.ai` is the CRM, `partners-pat.zenvoya.ai`
 * is the partner portal, `configs-pat.zenvoya.ai` is the legacy console, and all
 * three are the same build.
 *
 * ── Matched on the FIRST LABEL, by prefix ──────────────────────────────────
 * Every environment spells its host differently — `admin`, `admin-dev`,
 * `admin-pat`, `admin-prompt`, and the same fan-out for partners — so an exact
 * list would need editing for each new environment and would fail closed (to
 * the admin fallback) on the one nobody remembered. Prefix-matching the first
 * label covers the whole family at once:
 *
 *   admin·         admin.zenvoya.ai, admin-dev…, admin-pat…, admin-prompt…
 *   partner·       partners.zenvoya.ai, partners-pat…, and the legacy
 *                  `partner.` singular that shipped before this
 *   dashboard·     a partner's own white-label domain — dashboard.navitravel.ai,
 *                  dashboard-dev.navitravel.ai… — is the partner portal too
 *   configs·       configs.zenvoya.ai, configs-pat…
 *
 * Only the FIRST label is read, so a path or a deeper domain cannot smuggle a
 * keyword in: `partners.zenvoya.ai` matches on `partners`, while
 * `secure.partners.zenvoya.ai` has the first label `secure` and falls through.
 *
 * `www.` is stripped first — `www.admin-pat.zenvoya.ai` and
 * `admin-pat.zenvoya.ai` are the same deployment and must not disagree.
 *
 * ── "partner", not "partners" ──────────────────────────────────────────────
 * "partners" starts with "partner", so one prefix serves both spellings. The
 * hosts this replaces were documented as `partner.zenvoya.com`, and a rename
 * that silently sent an old host to the CRM login would be a lockout.
 *
 * ── An unmatched host is UNFENCED, and that is load-bearing ────────────────
 * `localhost:4100` has no subdomain to read, and neither does an Amplify
 * preview or branch host. Those fall back to the CRM as the landing route AND
 * are exempt from the enforcement below — otherwise local development could
 * only ever reach `/admin`, and nobody could work on the partner portal.
 */
const HOST_PORTALS = [
  { prefix: "admin", route: ROUTES.admin },
  { prefix: "partner", route: ROUTES.partnerAdmin },
  { prefix: "dashboard", route: ROUTES.partnerAdmin },
  { prefix: "configs", route: ROUTES.legacyAdmin },
];

/**
 * Which top-level path prefixes belong to which portal, for enforcement.
 *
 * The legacy console owns three of them: its own `/legacyAdmin`, the sign-in it
 * bounces to (`/auth`, `views/auth/SignIn.jsx`) and the `/rtl` demo layout. They
 * travel together — fencing `/legacyAdmin` while leaving `/auth` reachable would
 * leave the legacy login on a partner-facing host, which is half a fence.
 */
const PORTAL_PATHS = {
  [ROUTES.admin]: ["/admin"],
  [ROUTES.partnerAdmin]: ["/partnerAdmin"],
  [ROUTES.legacyAdmin]: ["/legacyAdmin", "/auth", "/rtl"],
};

/**
 * Paths every host serves whatever portal it is fenced to.
 *
 * `/resetpassword/:id` is a one-time deep link from an email. Nothing in either
 * repo builds one today, but bouncing a password-reset link is a lockout with
 * no upside, so it stays reachable everywhere.
 */
const SHARED_PATHS = ["/resetpassword"];

/** The subdomain that decides the portal: first label, lowercased, `www.` dropped. */
export const portalHostLabel = (hostname) =>
  String(hostname || "")
    .toLowerCase()
    .replace(/^www\./, "")
    .split(".")[0];

/** The matched host family, or null when the host names no portal (localhost, previews). */
const portalForHost = (hostname) => {
  const label = portalHostLabel(hostname);
  return HOST_PORTALS.find((h) => label.startsWith(h.prefix)) || null;
};

/** The portal route for a hostname. Exported for the tests; prefer the no-arg call. */
export const routeForHost = (hostname) => portalForHost(hostname)?.route || ROUTES.admin;

const ownsPath = (prefixes, pathname) =>
  prefixes.some((p) => pathname === p || pathname.startsWith(`${p}/`));

/**
 * Where a request must be sent because this HOST does not serve that portal —
 * or null when the path is fine here.
 *
 * Returns the host's own landing route rather than a dead end: someone who
 * followed a stale `admin-pat…/partnerAdmin/login` link should arrive at a
 * console they can use, not a blank page.
 *
 * Silent on every path no portal claims (`/`, an unknown URL, the shared reset
 * link) so this can never turn a 404 into a redirect loop.
 *
 * ⚠️ ALWAYS ON — there is no opt-out, by decision. Two things therefore have to
 * be true of every environment this ships to, and NEITHER is enforced by code.
 * Both were open when this landed (2026-09-25):
 *
 *   - THE OTHER PORTALS' HOSTS MUST EXIST. A partner user bounced off an
 *     `admin.*` host has nowhere to go if `partners.<env>.zenvoya.ai` does not
 *     resolve. `partners-pat` and `configs-pat` do; `partners.zenvoya.ai`,
 *     `partners-dev` and `partners-prompt` do NOT.
 *   - APP-BACKEND'S PARTNER EMAILS MUST POINT AT THE PARTNER HOST.
 *     `emailService.sendCredentialEmail` and `TenantController` both build
 *     `PARTNER_PORTAL_URL + "/partnerAdmin/login"`, and that variable is an
 *     ADMIN host (`https://admin-pat.zenvoya.ai`). Every partner credential,
 *     invite and reset link therefore now lands on a host that redirects it to
 *     the CRM login, which a partner user cannot sign in to. Repointing it is
 *     not enough on its own: the SAME variable also builds the CRM's own
 *     `/admin/login` links, so the origin has to be split in two (tracked as
 *     follow-up work, deliberately not done here).
 *
 * The API is unaffected either way — a partner reaching the CRM login is still
 * refused server-side by the portal check in PartnerAuthRepository. What is at
 * stake here is whether a person can find the door, not what is behind it.
 */
export const portalRedirectFor = (pathname, hostname) => {
  const host = portalForHost(hostname);
  if (!host) return null; // unmatched host — see "UNFENCED" above

  const path = String(pathname || "");
  if (ownsPath(SHARED_PATHS, path)) return null;

  const trespass = Object.entries(PORTAL_PATHS).some(
    ([portal, prefixes]) => portal !== host.route && ownsPath(prefixes, path),
  );
  return trespass ? host.route : null;
};

/** `portalRedirectFor` against the live location. Null when nothing should move. */
export const hostPortalRedirect = (pathname) => {
  if (typeof window === "undefined") return null;
  return portalRedirectFor(pathname, window.location.hostname);
};

export const getDefaultRouteForHost = () => {
  if (typeof window === "undefined") return ROUTES.admin;
  return routeForHost(window.location.hostname);
};
